import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CspSessionType } from 'src/app/CspSignin/redux/cspSignin.reducer'
import EmailVerification from 'src/assets/images/EmailVerification.svg'
import Verification from 'src/assets/images/Verification.svg'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import RadioItems from 'src/components/RadioItems/RadioItems'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import TextWithTooltipOnClick, {
  TextWithTooltipOnClickSize,
} from 'src/components/TextWithTooltipOnClick/TextWithTooltipOnClick'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import UserVerificationInputField from 'src/components/UserVerificationInputField/UserVerificationInputField'
import { AvatarSizes } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CspUserListModel } from 'src/models/cspUser.models'
import { IssuerListModel } from 'src/models/issuer.models'
import { UserListModel } from 'src/models/user.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type CustomerEmailFlatFields = {
  email: string
}

export type LiveCustomerCodeVerificationFlatFields = {
  code: string
}
interface CspStartSessionBoxProps {
  className?: string
  user?: UserListModel
  cspUser?: CspUserListModel
  password?: string
  defaultCspIssuer?: IssuerListModel
  selectedCspSessionType?: CspSessionType
  setSelectedCspSessionType: (t?: CspSessionType) => void
  postCspEmulatedSessionsOffline: (email: string, password?: string) => void
  isPostingCspEmulatedSessionsOffline: boolean
  postCspEmulatedSessionsOfflineTopLevelError?: string
  postCspEmulatedSessionsOfflineErrorDetailMap?: ErrorDetailMap<string>
  postCspEmulatedSessionsLive: (email: string, password?: string) => void
  isPostingCspEmulatedSessionsLive: boolean
  postCspEmulatedSessionsLiveTopLevelError?: string
  postCspEmulatedSessionsLiveErrorDetailMap?: ErrorDetailMap<string>
  putCspEmulatedSessionsVerify: (vc: string) => void
  isPuttingCspEmulatedSessionsVerify: boolean
  putCspEmulatedSessionsVerifyTopLevelError?: string
  putCspEmulatedSessionsVerifyErrorDetailMap?: ErrorDetailMap<string>
  hasSentToken: boolean
  putCspEmulatedSessionsResend: () => void
}

const CspStartSessionBox: React.FC<CspStartSessionBoxProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const appName = useSelector(selectAppName)
  const translate = useTranslate()

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [hasConfirmedEmail, setHasConfirmedEmail] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')

  useEffect((): void => {
    setSelectedIndex(0)
    setHasConfirmedEmail(false)
    setEmail('')
    onCustomerEmailFlatFieldChange('email', '')
  }, [props.selectedCspSessionType])

  const isCodeVerification =
    props.selectedCspSessionType === CspSessionType.Live &&
    props.user !== undefined

  const customerEmailFlatFieldsValidation =
    (): FormFieldsValidation<CustomerEmailFlatFields> => ({
      email: [stringIsEmpty()],
    })

  const {
    fieldsErrors: customerEmailFlatFieldsErrors,
    onFieldChange: onCustomerEmailFlatFieldChange,
    setErrorsAndReturnDidPassValidation: customerEmailFlatFieldsSetErrors,
  } = useFormFields<CustomerEmailFlatFields>(
    {
      email: '',
    },
    customerEmailFlatFieldsValidation,
    undefined,
  )

  const liveCustomerCodeVerificationFlatFieldsValidation =
    (): FormFieldsValidation<LiveCustomerCodeVerificationFlatFields> => ({
      code: [stringIsEmpty()],
    })

  const {
    fieldsErrors: liveCustomerCodeVerificationFlatFieldsErrors,
    onFieldChange: onLiveCustomerCodeVerificationFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      liveCustomerCodeVerificationFlatFieldsSetErrors,
  } = useFormFields<LiveCustomerCodeVerificationFlatFields>(
    {
      code: '',
    },
    liveCustomerCodeVerificationFlatFieldsValidation,
    undefined,
  )

  return (
    <Box
      noPadding
      css={css`
        position: relative;
        display: flex;
        overflow: hidden;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          flex-direction: column;
        `)}
      `}
    >
      <div
        css={css`
          position: relative;
          padding: 80px;
          background-color: ${styleConstants.colors.white};
          display: flex;
          flex-direction: column;
          width: 550px;

          ${getBreakpointDown(breakpointSizes.SM)(css`
            width: auto;
            padding: 60px 40px;
          `)}

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding: 40px 20px;
          `)}
        `}
      >
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            overflow: hidden;
          `}
        >
          <img
            src={isCodeVerification ? Verification : EmailVerification}
            alt={''}
            css={css`
              transform: ${isCodeVerification
                ? 'translate(-30px, -70px) scale(1.2)'
                : 'translate(-70px, -70px)'};
              height: 150px;
            `}
          />
        </div>
        {props.selectedCspSessionType === undefined && (
          <Fragment>
            <h2
              css={css`
                margin-bottom: 40px;
                font-weight: 500;
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  margin-top: 15px;
                `)}
              `}
            >
              {translate('Choose session type')}
            </h2>
            <RadioItems
              css={css`
                margin-bottom: 40px;
              `}
              gap="10px"
              items={[
                <span
                  key={0}
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('Live Session')}
                </span>,
                <span
                  key={1}
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('Offline Session')}
                </span>,
              ]}
              setIndex={setSelectedIndex}
              currentIndex={selectedIndex}
              grayBackground
              ariaLabels={[
                translate('Live Session'),
                translate('Offline Session'),
              ]}
            />
            <Button
              type={ButtonTypes.button}
              design={ButtonDesigns.designCta}
              text={translate('Begin Session')}
              big
              onClick={(): void => {
                props.setSelectedCspSessionType(
                  selectedIndex === 0
                    ? CspSessionType.Live
                    : selectedIndex === 1
                      ? CspSessionType.Offline
                      : undefined,
                )
              }}
            />
          </Fragment>
        )}
        {props.selectedCspSessionType === CspSessionType.Offline && (
          <form
            css={css`
              display: flex;
              flex-direction: column;
            `}
            onSubmit={(e): void => {
              e.preventDefault()
              if (customerEmailFlatFieldsSetErrors()) {
                props.postCspEmulatedSessionsOffline(email, props.password)
              }
            }}
          >
            <h2
              css={css`
                margin-bottom: 40px;
                font-weight: 500;
                ${getFontCss(
                  32,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  margin-top: 15px;
                `)}
              `}
            >
              {translate('Verify offline customer: Email confirmation')}
            </h2>
            <TextInputField
              fieldLabel={translate('Customer Email')}
              design={TextInputFieldDesigns.New}
              css={css`
                margin-bottom: 20px;
              `}
              value={email}
              placeholder={translateSingleString(
                locale,
                'Enter customer email for their ${appName} profile',
                { appName: translateSingleString(locale, appName) },
              )}
              onChange={(val): void => {
                onCustomerEmailFlatFieldChange('email', val)
                setEmail(val)
              }}
              type={'email'}
              autoComplete={'email'}
              errors={
                customerEmailFlatFieldsErrors['email'] ||
                props.postCspEmulatedSessionsOfflineErrorDetailMap?.['email']
              }
            />
            <Checkbox
              css={css`
                margin-bottom: 40px;
              `}
              isSelected={hasConfirmedEmail}
              label={translate(
                "I have confirmed this person's identity through appropriate bank procedures",
              )}
              onClick={(): void => {
                setHasConfirmedEmail((val) => !val)
              }}
            />
            <Button
              type={ButtonTypes.submit}
              design={ButtonDesigns.designCta}
              text={translate('Confirm Email and Start Emulation')}
              big
              disabled={!hasConfirmedEmail}
              loading={props.isPostingCspEmulatedSessionsOffline}
            />
            {props.postCspEmulatedSessionsOfflineTopLevelError !==
              undefined && (
              <TopLevelError
                css={css`
                  margin-top: 20px;
                `}
                error={props.postCspEmulatedSessionsOfflineTopLevelError}
              />
            )}
          </form>
        )}
        {props.selectedCspSessionType === CspSessionType.Live &&
          props.user === undefined && (
            <form
              css={css`
                display: flex;
                flex-direction: column;
              `}
              onSubmit={(e): void => {
                e.preventDefault()
                if (customerEmailFlatFieldsSetErrors()) {
                  props.postCspEmulatedSessionsLive(email, props.password)
                }
              }}
            >
              <h2
                css={css`
                  margin-bottom: 40px;
                  font-weight: 500;
                  ${getFontCss(
                    32,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};

                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    margin-top: 15px;
                  `)}
                `}
              >
                {translate('Verify live customer: Email confirmation')}
              </h2>
              <TextInputField
                fieldLabel={translate('Customer Email')}
                design={TextInputFieldDesigns.New}
                css={css`
                  margin-bottom: 20px;
                `}
                value={email}
                placeholder={translateSingleString(
                  locale,
                  'Enter customer email for their ${appName} profile',
                  { appName: translateSingleString(locale, appName) },
                )}
                onChange={(val): void => {
                  onCustomerEmailFlatFieldChange('email', val)
                  setEmail(val)
                }}
                type={'email'}
                autoComplete={'email'}
                errors={
                  customerEmailFlatFieldsErrors['email'] ||
                  props.postCspEmulatedSessionsLiveErrorDetailMap?.['email']
                }
              />
              <Button
                type={ButtonTypes.submit}
                design={ButtonDesigns.designCta}
                text={translate('Confirm Email and Send Code')}
                big
                loading={props.isPostingCspEmulatedSessionsLive}
              />
              {props.postCspEmulatedSessionsLiveTopLevelError !== undefined && (
                <TopLevelError
                  css={css`
                    margin-top: 20px;
                  `}
                  error={props.postCspEmulatedSessionsLiveTopLevelError}
                />
              )}
            </form>
          )}
        {props.selectedCspSessionType === CspSessionType.Live &&
          props.user !== undefined && (
            <form
              css={css`
                display: flex;
                flex-direction: column;
              `}
              onSubmit={(e): void => {
                e.preventDefault()
                if (liveCustomerCodeVerificationFlatFieldsSetErrors()) {
                  props.putCspEmulatedSessionsVerify(code)
                }
              }}
            >
              <h2
                css={css`
                  margin-bottom: 40px;
                  font-weight: 500;
                  ${getFontCss(
                    32,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};

                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    margin-top: 15px;
                  `)}
                `}
              >
                {translate('Verify live customer: Code verification')}
              </h2>
              <div
                css={css`
                  padding: 20px;
                  background-color: ${styleConstants.colors.gray_light};
                  display: flex;
                  border-radius: $${styleConstants.borderRadius.box};
                  margin-bottom: 40px;
                `}
              >
                <UserAvatar user={props.user} size={AvatarSizes.Medium} />
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    margin-left: 10px;

                    h3 {
                      ${getFontCss(
                        20,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      font-weight: normal;
                    }

                    span {
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      margin-top: 4px;
                    }
                  `}
                >
                  <h3>{props.user.fullNameOrEmail}</h3>
                  {props.user.hasFullName && <span>{props.user.email}</span>}
                  {props.cspUser && (
                    <span>
                      {props.cspUser.companies.map((company, i) =>
                        i === props.cspUser!!.companies.length - 1
                          ? company
                          : `${company}, `,
                      )}
                    </span>
                  )}
                </div>
              </div>
              <UserVerificationInputField
                css={css`
                  margin-bottom: 40px;
                `}
                fieldLabel={translate('Verification Code')}
                value={code}
                onChange={(val): void => {
                  onLiveCustomerCodeVerificationFlatFieldChange('code', val)
                  setCode(val)
                }}
                errors={
                  liveCustomerCodeVerificationFlatFieldsErrors['code'] ||
                  props.putCspEmulatedSessionsVerifyErrorDetailMap?.[
                    'verificationCode'
                  ] ||
                  props.putCspEmulatedSessionsVerifyTopLevelError
                }
              />
              <Button
                type={ButtonTypes.submit}
                design={ButtonDesigns.designCta}
                text={translate('Verify Code and Start Emulating')}
                big
                loading={props.isPuttingCspEmulatedSessionsVerify}
              />
              <TextWithTooltipOnClick
                css={css`
                  align-self: center;
                  margin-top: 30px;
                `}
                text={translate('Send new code')}
                tooltipBody={translate('New code sent!')}
                onClick={(): void => {
                  props.putCspEmulatedSessionsResend()
                }}
                delayTrigger
                triggerNow={props.hasSentToken}
                size={TextWithTooltipOnClickSize.Medium}
                underline
              />
            </form>
          )}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 410px;
          padding: 80px 40px;
          background-color: ${styleConstants.colors.gray_light};

          ${getBreakpointDown(breakpointSizes.SM)(css`
            padding: 60px 40px;
          `)}

          h3 {
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          }

          p {
            &:nth-child(n + 2) {
              margin-top: 20px;
            }
            span {
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            }
          }
        `}
      >
        {props.selectedCspSessionType === undefined && (
          <Fragment>
            <h3>{translate('Instructions')}</h3>
            <p>
              {translate(
                "Choose the nature of the emulation session. If you're actively speaking with a customer, choose ${liveSession}. If not directly speaking with the customer, choose ${offlineSession}.",
                {
                  liveSession: <span>{translate('"Live Session"')}</span>,
                  offlineSession: <span>{translate('"Offline Session"')}</span>,
                },
              )}
            </p>
          </Fragment>
        )}
        {props.selectedCspSessionType === CspSessionType.Offline && (
          <Fragment>
            <h3>{translate('Offline session')}</h3>
            <p>
              {translate(
                'Your customer support account only permits you to help ${appName} users associated with ${issuerName}.',
                {
                  appName: <AppName />,
                  issuerName: <span>{props.defaultCspIssuer?.name}</span>,
                },
              )}
            </p>
            <div
              css={css`
                margin-top: 20px;
                height: 1px;
                background-color: ${styleConstants.colors.gray_medium};
              `}
            />
            <p>
              {translate(
                '${ifTheCustomer} please enter the email they used to reach out to you',
                {
                  ifTheCustomer: (
                    <span>
                      {translate('If the customer reached out via email,')}
                    </span>
                  ),
                },
              )}
            </p>
            <p>
              {translate(
                '${ifViaVoicemail} you will need to confirm their identity through other means before entering their email.',
                {
                  ifViaVoicemail: <span>{translate('If via voicemail,')}</span>,
                },
              )}
            </p>
          </Fragment>
        )}
        {props.selectedCspSessionType === CspSessionType.Live &&
          props.user === undefined && (
            <Fragment>
              <h3>{translate('Live session (step 1 of 2)')}</h3>
              <p>
                {translate(
                  'Your customer support account only permits you to help ${appName} users associated with ${issuerName}.',
                  {
                    appName: <AppName />,
                    issuerName: <span>{props.defaultCspIssuer?.name}</span>,
                  },
                )}
              </p>
              <div
                css={css`
                  margin-top: 20px;
                  height: 1px;
                  background-color: ${styleConstants.colors.gray_medium};
                `}
              />
              <p>
                {translate(
                  '${pleaseAskThe} for the email they used to sign up with ${appName}.',
                  {
                    appName: <AppName />,
                    pleaseAskThe: (
                      <span>{translate('Please ask the customer')}</span>
                    ),
                  },
                )}
              </p>
              <p>
                {translate(
                  '${pressTheConfirm} to send a code to their email address.',
                  {
                    pressTheConfirm: (
                      <span>{translate('Press the "Confirm..." button')}</span>
                    ),
                  },
                )}
              </p>
            </Fragment>
          )}
        {props.selectedCspSessionType === CspSessionType.Live &&
          props.user !== undefined && (
            <Fragment>
              <h3>{translate('Live session (step 2 of 2)')}</h3>
              <p>
                {translate(
                  'A verification code has been sent to the following email: ${email}',
                  {
                    email: <span>{props.user?.email}</span>,
                  },
                )}
              </p>
              <div
                css={css`
                  margin-top: 20px;
                  height: 1px;
                  background-color: ${styleConstants.colors.gray_medium};
                `}
              />
              <p>
                {translate(
                  '${theCustomerNeeds} from Extend, and repeat the 6-digit code',
                  {
                    theCustomerNeeds: (
                      <span>
                        {translate(
                          'The customer needs to find the "Verification Code" email',
                        )}
                      </span>
                    ),
                  },
                )}
              </p>
            </Fragment>
          )}
      </div>
    </Box>
  )
}

export default CspStartSessionBox
